import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '@/api/settings.api';
import { useSession } from '@/app/session';
import { DEFAULT_BLOCK_KEYWORDS, DEFAULT_WEEK_START, type WeekStart } from '@/lib/calendar';

/**
 * The clinic's calendar behaviour, read once and handed to every screen that
 * draws or counts a booking.
 *
 * These are not per-person preferences — the overlap style or the day's zoom
 * belong to whoever is looking — but decisions the whole team's grid depends
 * on: how many minutes late a patient is before reception is asked to phone,
 * which words mark a chair-block rather than a patient, and where the week
 * begins. They ride on `GET /settings/look` for the same reason the statuses
 * do: the people who live in the calendar — reception, doctors, assistants —
 * do not hold `setari.vede`, so they can never read the full settings.
 *
 * The defaults reproduce the behaviour from before the settings existed, so a
 * clinic that never opens the screen sees exactly what it always did.
 */
export interface CalendarPrefs {
  /** Minutes past the start after which an un-arrived, still-expected patient pulses. */
  overdueMin: number;
  /** Word-starts that mark a schedule block; empty means the built-in set. */
  blockKeywords: readonly string[];
  /** Which weekday the week begins on: 1 = Monday (default), 0 = Sunday. */
  weekStart: WeekStart;
}

const DEFAULTS: CalendarPrefs = {
  overdueMin: 15,
  blockKeywords: DEFAULT_BLOCK_KEYWORDS,
  weekStart: DEFAULT_WEEK_START,
};

const Ctx = createContext<CalendarPrefs>(DEFAULTS);

export function CalendarPrefsProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const q = useQuery({
    queryKey: ['settings', 'look'],
    queryFn: settingsApi.look,
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  const data = q.data;
  const value = useMemo<CalendarPrefs>(() => {
    if (!data) return DEFAULTS;
    const kws = Array.isArray(data.blockKeywords)
      ? data.blockKeywords.filter((k) => typeof k === 'string' && k.trim() !== '')
      : [];
    return {
      overdueMin:
        Number.isFinite(data.calOverdueMin) && data.calOverdueMin >= 1 ? data.calOverdueMin : 15,
      blockKeywords: kws.length > 0 ? kws : DEFAULT_BLOCK_KEYWORDS,
      weekStart: data.weekStart === 0 ? 0 : 1,
    };
  }, [data]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The clinic's calendar behaviour; the defaults where it has chosen nothing. */
export function useCalendarPrefs(): CalendarPrefs {
  return useContext(Ctx);
}
